import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const CITIES = [
  {
    name: "Dublin",
    slug: "/es/dublin",
    description: "Emerald Glen, Fallon Village, Jordan Ranch, West Dublin, Positano",
    highlight: "Familias jóvenes y comunidades residenciales modernas",
  },
  {
    name: "Livermore",
    slug: "/es/livermore",
    description: "Downtown Livermore, Springtown, Jensen Tract, South Livermore, Sunset West",
    highlight: "Hogares familiares en el corazón del Tri-Valley",
  },
  {
    name: "Pleasanton",
    slug: "/es/pleasanton",
    description: "Downtown Pleasanton, Ruby Hill, Vintage Hills, Birdland, Hacienda Business Park",
    highlight: "Vecindarios premiados y comunidades de lujo",
  },
  {
    name: "San Ramon",
    slug: "/es/san-ramon",
    description: "Bishop Ranch, Crow Canyon, Dougherty Valley, Norris Canyon, Twin Creeks",
    highlight: "Profesionales y familias en comunidades cerradas",
  },
];

const CityGrid = () => {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <section className="py-20 bg-background">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-14 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ciudades que Servimos en el Tri-Valley</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Ofrecemos limpieza profesional del hogar en las cuatro ciudades principales del Tri-Valley.
            Selecciona tu ciudad para ver información específica de tu área.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CITIES.map((city, i) => (
            <Link
              key={i}
              to={city.slug}
              className={cn(
                "group bg-card border rounded-2xl p-7 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 flex flex-col gap-4 anim-fade-up",
                isVisible && "is-visible"
              )}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold group-hover:text-primary transition-colors">{city.name}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{city.description}</p>
              <p className="text-xs font-semibold text-primary/80 italic">{city.highlight}</p>
              <div className="mt-auto flex items-center gap-1 text-sm font-bold text-primary">
                Ver página <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg w-full sm:w-auto">
            <a href={BOOKING_URL}>Reservar Ahora</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl w-full sm:w-auto">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};



const TriValleySpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "tri-valley", name: "Tri-Valley", type: "city", regionSlug: "alameda" }}>
      <SEO
        title="Limpieza del Hogar en el Tri-Valley, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Dublin, Livermore, Pleasanton y San Ramon. Precios planos sin sorpresas, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar tri-valley, servicio de limpieza dublin ca, maid service pleasanton español, limpieza livermore ca, limpieza san ramon, empleada doméstica tri-valley, limpieza de casas condado alameda"
        url="https://uplevelcleaning.com/es/tri-valley"
        alternateUrl="https://uplevelcleaning.com/locations/alameda"
        alternateLang="en-US"
        locationName="Tri-Valley"
        regionSlug="alameda"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Condado de Alameda", url: "/es/alameda" },
          { name: "Tri-Valley", url: "/es/tri-valley" },
        ]}
        reviews={[
          { author: "Lorena M.", rating: 5, reviewBody: "Vivo en Pleasanton y llevaba mucho tiempo buscando un buen servicio. ¡Uplevel Cleaning superó todas mis expectativas! Puntual, profesional y detallista.", datePublished: "2026-03-25" },
          { author: "Andrés V.", rating: 5, reviewBody: "Contraté la limpieza profunda en Dublin y el equipo fue increíble. Mi casa quedó impecable y el precio fue exactamente el que se indicó en línea.", datePublished: "2026-02-20" },
          { author: "Patricia S.", rating: 5, reviewBody: "Llevo 5 meses con el servicio quincenal en Livermore. Siempre puntuales, siempre perfectos. El mejor servicio de limpieza del Tri-Valley sin duda alguna.", datePublished: "2026-01-18" },
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="el Tri-Valley" regionSlug="alameda" />

          {/* Trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Dublin · Livermore · Pleasanton · San Ramon",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
                "Asegurados y Bonificados",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <CityGrid />
          <SpanishWhyUs cityName="el Tri-Valley" />
          <SpanishServices />
          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="oakland" />
          <Guarantee />
          <SpanishFAQ cityName="el Tri-Valley" />
          <InternalLinks />
          <SpanishFinalCTA cityName="el Tri-Valley" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default TriValleySpanishPage;
